/**
 * Nexus Core Telemetry Hub (v1.2)
 * Consolidates Sentry and Datadog into a single agentic observability layer.
 */

export const initTelemetry = async () => {
  const isProd = import.meta.env.PROD;
  const telemetryEnabled = import.meta.env.VITE_TELEMETRY_ENABLED !== "false";
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  const datadogApplicationId = import.meta.env.VITE_DATADOG_APPLICATION_ID;
  const datadogClientToken = import.meta.env.VITE_DATADOG_CLIENT_TOKEN;
  const datadogSite = import.meta.env.VITE_DATADOG_SITE || "us5.datadoghq.com";
  const datadogService = import.meta.env.VITE_DATADOG_SERVICE || "pluggedin-marketing";
  const datadogEnv = import.meta.env.VITE_DATADOG_ENV || (isProd ? "prod" : "dev");

  if (!telemetryEnabled) {
    return;
  }

  // Keep the first render light. Observability can initialise just after the
  // app becomes interactive instead of being part of the critical bundle.
  await new Promise<void>((resolve) => {
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(() => resolve(), { timeout: 2_000 });
      return;
    }
    window.setTimeout(resolve, 1_000);
  });

  // 1. Sentry Initialization (Errors & Performance)
  if (sentryDsn) {
    const Sentry = await import("@sentry/react");
    Sentry.init({
      dsn: sentryDsn,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      // Performance Monitoring
      tracesSampleRate: 1.0,
      // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
      tracePropagationTargets: ["localhost", /^https:\/\/thestudentnetworkuk\.co\.uk/, /^https:\/\/aeiefffzkxndngudbpll\.supabase\.co/],
      // Session Replay
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      environment: isProd ? "production" : "development",
    });
  }

  // 2. Datadog RUM Initialization (Real User Monitoring)
  if (datadogApplicationId && datadogClientToken) {
    const [{ datadogRum }, { datadogLogs }] = await Promise.all([
      import("@datadog/browser-rum"),
      import("@datadog/browser-logs"),
    ]);

    datadogRum.init({
      applicationId: datadogApplicationId,
      clientToken: datadogClientToken,
      site: datadogSite,
      service: datadogService,
      env: datadogEnv,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: "mask-user-input",
    });

    datadogLogs.init({
      clientToken: datadogClientToken,
      site: datadogSite,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
    });
  }

};
